import {
  changeSetCommentRowFromGridKey,
  gridCellCommentKeys,
  isAs400WorksheetColumnKey,
  type As400ExportGroup,
  type ChangeSetCommentThread,
} from '@erp/rpc';
import { formatThreadAsNote } from '@erp/shared-utils';

/**
 * The review grid's side of cell comments (GEN-3263): which threads a grid
 * cell has, the corner marker that says so, and the hover preview. The grid
 * renders outside React, so the marker is painted on the cell as it renders,
 * read from an index built once per thread list.
 */

/** A review's comment threads by `anchor.cellKey`. */
export type CellThreadIndex = ReadonlyMap<
  string,
  readonly ChangeSetCommentThread[]
>;

export const NO_CELL_THREADS: CellThreadIndex = new Map();

export function indexThreadsByCell(
  threads: readonly ChangeSetCommentThread[],
): CellThreadIndex {
  const index = new Map<string, ChangeSetCommentThread[]>();
  for (const thread of threads) {
    const cell = index.get(thread.anchor.cellKey);
    if (cell) cell.push(thread);
    else index.set(thread.anchor.cellKey, [thread]);
  }
  return index;
}

/**
 * One grid cell's threads, oldest first. A joined Purchase Data row answers
 * to a comment on either of its sources (see `gridCellCommentKeys`).
 */
export function threadsForCell(
  index: CellThreadIndex,
  group: As400ExportGroup,
  gridRowKey: string,
  columnKey: string,
): ChangeSetCommentThread[] {
  return gridCellCommentKeys(group, gridRowKey, columnKey).flatMap(
    (cellKey) => index.get(cellKey) ?? [],
  );
}

/**
 * Whether a grid cell can take a comment: a saved row (not an unsaved draft)
 * and one of the worksheet's columns (not a spacer).
 */
export function isCommentableCell(
  group: As400ExportGroup,
  gridRowKey: string,
  columnKey: string,
): boolean {
  return (
    changeSetCommentRowFromGridKey(gridRowKey) !== null &&
    isAs400WorksheetColumnKey(group, columnKey)
  );
}

/**
 * What a cell's corner marker says: `open` while any of its threads is open,
 * `resolved` (drawn muted) once every one is resolved, nothing without threads.
 */
export type CellCommentMarker = 'open' | 'resolved';

export function cellCommentMarker(
  threads: readonly ChangeSetCommentThread[],
): CellCommentMarker | null {
  if (threads.length === 0) return null;
  return threads.some((thread) => thread.resolvedAt === null)
    ? 'open'
    : 'resolved';
}

/** Marks a rendered cell that has comments, holding its marker. */
export const COMMENT_CELL_ATTR = 'data-cell-comment';

// Excel-style corner triangle, as the header notes draw it, but in Excel's
// threaded-comment violet rather than the notes' --primary, and slate once
// every thread is resolved. Inline, like the cell tints: Handsontable renders
// outside Tailwind's pipeline. Painted as a background layer, so it needs no
// element of its own in a cell Handsontable rewrites on every render, and sits
// over the edit-state tint (a background colour).
const COMMENT_MARKER_COLORS: Record<CellCommentMarker, string> = {
  open: 'rgb(147, 51, 234)',
  resolved: 'rgb(148, 163, 184)',
};
// Along the gradient's diagonal: the triangle's sides come out at 6px, the
// header marker's size.
const COMMENT_MARKER_DEPTH = '4.25px';
// How far into the corner a click still counts as on the marker — a little
// more than the triangle, which is small to aim at.
const COMMENT_MARKER_HIT_PX = 10;

/**
 * Draw a cell's comment marker, or clear it. Handsontable recycles cells, so
 * every render does one or the other.
 */
export function paintCommentMarker(
  td: HTMLTableCellElement,
  marker: CellCommentMarker | null,
): void {
  if (!marker) {
    td.removeAttribute(COMMENT_CELL_ATTR);
    td.style.backgroundImage = '';
    return;
  }
  const color = COMMENT_MARKER_COLORS[marker];
  td.setAttribute(COMMENT_CELL_ATTR, marker);
  td.style.backgroundImage = `linear-gradient(225deg, ${color} ${COMMENT_MARKER_DEPTH}, transparent ${COMMENT_MARKER_DEPTH})`;
}

/** The marked cell under `target`, if any. */
export function commentedCellFrom(
  target: EventTarget | null,
): HTMLTableCellElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLTableCellElement>(`td[${COMMENT_CELL_ATTR}]`);
}

/** Whether a click at `point` lands on the top-right marker of `cell`. */
export function isOnCommentMarker(
  cell: { right: number; top: number },
  point: { clientX: number; clientY: number },
): boolean {
  return (
    point.clientX >= cell.right - COMMENT_MARKER_HIT_PX &&
    point.clientY <= cell.top + COMMENT_MARKER_HIT_PX
  );
}

/** A box on screen, as `getBoundingClientRect` gives it. */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where an open cell's comments sit after the grid scrolls: the cell's box
 * while any of it is still inside the grid's visible area, else null (the
 * cell scrolled away, so the comments close).
 */
export function followedCellRect(
  cell: ScreenRect | null,
  grid: ScreenRect,
): ScreenRect | null {
  if (!cell) return null;
  const inside =
    cell.left < grid.left + grid.width &&
    cell.left + cell.width > grid.left &&
    cell.top < grid.top + grid.height &&
    cell.top + cell.height > grid.top;
  if (!inside) return null;
  const { left, top, width, height } = cell;
  return { left, top, width, height };
}

/**
 * The names a thread carries — its authors' and the people it @mentions — by
 * user id, to draw its messages' mentions as `@Name`.
 */
export function threadUserNames(
  thread: Pick<ChangeSetCommentThread, 'messages'>,
): Map<string, string> {
  const names = new Map<string, string>();
  for (const message of thread.messages) {
    if (message.authorUserId && message.authorName) {
      names.set(message.authorUserId, message.authorName);
    }
    for (const mention of message.mentions) {
      if (mention.name) names.set(mention.userId, mention.name);
    }
  }
  return names;
}

/**
 * The hover preview of a cell's threads: each flattened as the Excel export's
 * cell note reads (mentions by name only), a blank line between threads.
 */
export function commentPreviewText(
  threads: readonly ChangeSetCommentThread[],
): string {
  return threads
    .map((thread) => {
      const names = threadUserNames(thread);
      return formatThreadAsNote(thread, (userId) => names.get(userId));
    })
    .join('\n\n');
}
